import { useState, useEffect, useMemo } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Eye, MagnifyingGlass, DownloadSimple, Spinner, ListMagnifyingGlass,
  CheckCircle, Sparkle, Warning,
} from "@phosphor-icons/react";

/**
 * Modal for previewing a doc template merged against any case. Solicitor-
 * only. Does NOT touch the case Documents tab — it just runs the merge
 * server-side and streams the result back so the user can spot-check
 * custom-tag mappings before generating on a real matter.
 *
 * Flow:
 *  1. Search/select a case (debounced server-side search via /cases?search=)
 *  2. Click "Run preview" → GET /document-templates/{id}/preview?case_id=…
 *     → blob URL → iframe srcdoc renders the PDF inline.
 *  3. "Download .pdf" / "Download .docx" buttons re-use the same endpoint
 *     with fmt=docx to grab the editable version.
 */
export default function TemplatePreviewDialog({ open, onClose, template }) {
  const [caseQuery, setCaseQuery] = useState("");
  const [cases, setCases] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState("");
  // "Diff" side panel — fetches the resolved tag/value map alongside the
  // preview render so solicitors can spot unresolved or misnamed custom
  // tags without scanning the whole document.
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffData, setDiffData] = useState(null); // { tags: [...], stats: {...} }
  const [diffLoading, setDiffLoading] = useState(false);

  // Reset state when the dialog closes / template changes so we don't
  // flash stale data on next open.
  useEffect(() => {
    if (!open) {
      setCaseQuery("");
      setCases([]);
      setSelectedCase(null);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setError("");
      setDiffData(null);
      setDiffOpen(false);
    }
  }, [open, template?.id]);

  // Debounced case search. We use `mine` scope by default — broader scope
  // is unlikely for a previewer-on-this-firm flow.
  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ scope: "mine", limit: "10" });
        if (caseQuery.trim()) params.set("search", caseQuery.trim());
        const { data } = await API.get(`/cases?${params.toString()}`);
        setCases(data || []);
      } catch {
        setCases([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [caseQuery, open]);

  const runPreview = async () => {
    if (!selectedCase || !template) return;
    setPreviewing(true);
    setError("");
    // Drop the previous blob if any so memory doesn't leak across previews.
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    // Kick the diff fetch in parallel so it's ready when the user opens the
    // side panel — diff is much cheaper than the LibreOffice PDF render.
    setDiffLoading(true);
    API.get(`/document-templates/${template.id}/preview-tags`, {
      params: { case_id: selectedCase.id },
    })
      .then(({ data }) => setDiffData(data))
      .catch(() => setDiffData(null))
      .finally(() => setDiffLoading(false));
    try {
      const res = await API.get(
        `/document-templates/${template.id}/preview`,
        { params: { case_id: selectedCase.id, fmt: "pdf" }, responseType: "blob", timeout: 0 },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPdfUrl(url);
    } catch (err) {
      // The error body comes back as a blob — try to decode it as JSON for
      // the actual server-side error message.
      let msg = "Preview failed";
      const data = err.response?.data;
      if (data && data.type !== "application/pdf") {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          msg = formatApiErrorDetail(parsed.detail) || parsed.detail || msg;
        } catch {
          // ignore
        }
      } else if (err.message) {
        msg = err.message;
      }
      setError(msg);
      toast.error(msg);
    } finally {
      setPreviewing(false);
    }
  };

  const downloadAs = async (fmt) => {
    if (!selectedCase || !template) return;
    try {
      const res = await API.get(
        `/document-templates/${template.id}/preview`,
        { params: { case_id: selectedCase.id, fmt }, responseType: "blob", timeout: 0 },
      );
      const mime = fmt === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${template.name} - ${selectedCase.title || "case"} (preview).${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Download .${fmt} failed`);
    }
  };

  const customTagCount = useMemo(
    () => Object.keys(template?.custom_tags || {}).length,
    [template],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col" data-testid="template-preview-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
            Preview merge: <span className="font-semibold">{template?.name}</span>
          </DialogTitle>
          {customTagCount > 0 && (
            <p className="text-[11px] text-slate-500">
              Will resolve {customTagCount} custom tag{customTagCount === 1 ? "" : "s"} configured on this template.
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3">
          {/* Step 1 — case picker */}
          <div className="space-y-1.5" data-testid="preview-case-picker">
            <Label className="text-xs font-medium">Pick a case to merge against</Label>
            <div className="relative">
              <MagnifyingGlass weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={caseQuery}
                onChange={(e) => setCaseQuery(e.target.value)}
                placeholder="Search by title, address, ref…"
                className="pl-8 h-8 text-xs"
                data-testid="preview-case-search-input"
              />
            </div>
            <div className="border border-slate-200 rounded-md max-h-44 overflow-y-auto bg-white">
              {searching ? (
                <p className="text-[11px] text-slate-400 p-2.5 text-center">Searching…</p>
              ) : cases.length === 0 ? (
                <p className="text-[11px] text-slate-400 p-2.5 text-center">No cases found.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {cases.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedCase(c)}
                        className={`w-full text-left px-2.5 py-1.5 hover:bg-slate-50 ${selectedCase?.id === c.id ? "bg-violet-50" : ""}`}
                        data-testid={`preview-case-option-${c.id}`}
                      >
                        <p className="text-xs font-semibold text-slate-800 truncate">{c.title}</p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {c.case_ref ? `${c.case_ref} · ` : ""}{c.property_address || "(no address)"}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Step 2 — run preview / inline iframe */}
          {selectedCase && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500">Selected case</p>
                <p className="text-xs font-semibold text-slate-800 truncate">{selectedCase.title}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  size="sm" className="h-7 text-xs gap-1"
                  onClick={runPreview} disabled={previewing}
                  data-testid="preview-run-btn"
                >
                  {previewing
                    ? <><Spinner weight="bold" className="w-3.5 h-3.5 animate-spin" /> Rendering…</>
                    : <><Eye weight="bold" className="w-3.5 h-3.5" /> Run preview</>}
                </Button>
                {(pdfUrl || diffData || diffLoading) && (
                  <Button
                    size="sm" variant={diffOpen ? "default" : "outline"}
                    className="h-7 text-xs gap-1"
                    onClick={() => setDiffOpen((v) => !v)}
                    data-testid="preview-diff-toggle"
                    title="Show resolved tag values side-by-side"
                  >
                    <ListMagnifyingGlass weight="bold" className="w-3.5 h-3.5" />
                    Diff
                    {diffData?.stats?.unresolved > 0 && (
                      <span className="ml-0.5 text-[10px] font-semibold px-1 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        {diffData.stats.unresolved}
                      </span>
                    )}
                  </Button>
                )}
                {pdfUrl && (
                  <>
                    <Button
                      size="sm" variant="outline" className="h-7 text-xs gap-1"
                      onClick={() => downloadAs("pdf")}
                      data-testid="preview-download-pdf-btn"
                    >
                      <DownloadSimple weight="bold" className="w-3.5 h-3.5" /> .pdf
                    </Button>
                    <Button
                      size="sm" variant="outline" className="h-7 text-xs gap-1"
                      onClick={() => downloadAs("docx")}
                      data-testid="preview-download-docx-btn"
                    >
                      <DownloadSimple weight="bold" className="w-3.5 h-3.5" /> .docx
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5" data-testid="preview-error">
              {error}
            </div>
          )}

          {(pdfUrl || diffOpen) && (
            <div className={`flex gap-3 ${diffOpen ? "" : "flex-col"}`}>
              {pdfUrl && (
                <iframe
                  title="Template preview"
                  src={pdfUrl}
                  className={`${diffOpen ? "flex-1" : "w-full"} h-[55vh] border border-slate-200 rounded-md bg-white`}
                  data-testid="preview-iframe"
                />
              )}
              {diffOpen && (
                <aside
                  className={`${pdfUrl ? "w-72" : "w-full"} h-[55vh] border border-slate-200 rounded-md bg-white overflow-y-auto`}
                  data-testid="preview-diff-panel"
                >
                  <div className="sticky top-0 bg-white border-b border-slate-200 px-2.5 py-1.5 z-10">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                      Resolved tags
                    </p>
                    {diffLoading ? (
                      <p className="text-[10px] text-slate-400">Loading…</p>
                    ) : diffData?.stats ? (
                      <p className="text-[10px] text-slate-500">
                        {diffData.stats.total} total ·{" "}
                        <span className="text-emerald-600 font-medium">{diffData.stats.standard} standard</span>{" · "}
                        <span className="text-violet-600 font-medium">{diffData.stats.custom} custom</span>
                        {diffData.stats.unresolved > 0 && (
                          <> · <span className="text-amber-600 font-medium">{diffData.stats.unresolved} unresolved</span></>
                        )}
                      </p>
                    ) : (
                      <p className="text-[10px] text-slate-400">Click Run preview to load.</p>
                    )}
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {(diffData?.tags || []).map((row) => {
                      const Icon = row.source === "unresolved" ? Warning : row.source === "custom" ? Sparkle : CheckCircle;
                      const color = row.source === "unresolved" ? "text-amber-600"
                        : row.source === "custom" ? "text-violet-600" : "text-emerald-600";
                      return (
                        <li key={row.tag} className="px-2.5 py-1.5" data-testid={`diff-row-${row.tag}`}>
                          <div className="flex items-start gap-1.5">
                            <Icon weight={row.source === "unresolved" ? "fill" : "bold"} className={`w-3 h-3 mt-0.5 shrink-0 ${color}`} />
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-mono text-slate-800 break-all">{`{{${row.tag}}}`}</p>
                              {row.source === "custom" && row.mapped_path && (
                                <p className="text-[9px] text-violet-500 font-mono">→ {row.mapped_path}</p>
                              )}
                              <p className={`text-[11px] break-words mt-0.5 ${row.value ? "text-slate-700" : "text-slate-400 italic"}`}>
                                {row.value || (row.source === "unresolved" ? "(will stay as raw {{tag}} in output)" : "(empty)")}
                              </p>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </aside>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="preview-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
